import type { PointPort } from './port.model';

/** Fixed offset distance (board units) for angle center ports */
const ANGLE_CENTER_OFFSET = 0.8;

/**
 * Compute the angle center point — a point along the angle bisector,
 * offset inward from the vertex.
 *
 * @param vertex - The vertex of the angle (where the two arms meet)
 * @param arm1 - Endpoint of the first arm
 * @param arm2 - Endpoint of the second arm
 * @param offset - Distance from the vertex along the bisector (default 0.8)
 * @returns A PointPort [x, y] along the bisector
 */
export function computeAngleCenter(
  vertex: { X: () => number; Y: () => number },
  arm1: { X: () => number; Y: () => number },
  arm2: { X: () => number; Y: () => number },
  offset: number = ANGLE_CENTER_OFFSET,
): PointPort {
  const vx = vertex.X();
  const vy = vertex.Y();

  // Direction vectors from vertex to each arm endpoint
  const dx1 = arm1.X() - vx;
  const dy1 = arm1.Y() - vy;
  const dx2 = arm2.X() - vx;
  const dy2 = arm2.Y() - vy;

  // Normalize each direction
  const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
  const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

  if (len1 === 0 || len2 === 0) {
    return [vx, vy];
  }

  const nx1 = dx1 / len1;
  const ny1 = dy1 / len1;
  const nx2 = dx2 / len2;
  const ny2 = dy2 / len2;

  // Bisector direction = sum of normalized directions
  let bx = nx1 + nx2;
  let by = ny1 + ny2;
  const bLen = Math.sqrt(bx * bx + by * by);

  if (bLen === 0) {
    // Arms are opposite (180° angle) — use perpendicular
    bx = -ny1;
    by = nx1;
  } else {
    bx /= bLen;
    by /= bLen;
  }

  return [vx + bx * offset, vy + by * offset];
}
